import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class TokenRefreshService {
  constructor() {
    this.refreshInterval = null;
    this.isRefreshing = false;
    this.refreshIntervalMinutes = 20; // Refresh every 20 minutes
  }

  /**
   * Start automatic token refresh for admin users
   */
  startAutoRefresh() {
    // Clear any existing interval
    this.stopAutoRefresh();
    
    // Only start if admin token exists
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      console.log('No admin token found, skipping auto-refresh');
      return;
    }

    console.log(`Starting admin token auto-refresh every ${this.refreshIntervalMinutes} minutes`);
    
    // Set interval to refresh token every 20 minutes
    this.refreshInterval = setInterval(() => {
      this.refreshAdminToken();
    }, this.refreshIntervalMinutes * 60 * 1000);

    // Also refresh immediately to extend current session
    setTimeout(() => {
      this.refreshAdminToken();
    }, 5000); // Wait 5 seconds after login
  }

  /**
   * Stop automatic token refresh
   */
  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Admin token auto-refresh stopped');
    }
  }

  /**
   * Manually refresh admin token
   */
  async refreshAdminToken() {
    if (this.isRefreshing) {
      console.log('Token refresh already in progress, skipping');
      return;
    }

    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      console.log('No admin token to refresh');
      this.stopAutoRefresh();
      return;
    }

    this.isRefreshing = true;

    try {
      console.log('Refreshing admin token...');
      
      const response = await axios.post(`${API}/admin/auth/refresh-token`, {}, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const { access_token } = response.data;
      
      // Update stored token
      localStorage.setItem('admin_token', access_token);
      
      // Update axios default header if this is the current admin session
      if (axios.defaults.headers.common['Authorization'] === `Bearer ${adminToken}`) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }

      console.log('✅ Admin token refreshed successfully');
      
    } catch (error) {
      console.error('❌ Failed to refresh admin token:', error);
      
      // If refresh fails with 401, the session is truly expired
      if (error.response?.status === 401) {
        console.log('Admin session expired, stopping auto-refresh');
        this.stopAutoRefresh();
        
        // Clear admin token
        localStorage.removeItem('admin_token');
        
        // Redirect to admin login if currently on admin page
        if (window.location.pathname.includes('/admin')) {
          window.location.href = '/admin/login';
        }
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Check if user is admin and start refresh if needed
   */
  checkAndStartRefresh() {
    const adminToken = localStorage.getItem('admin_token');
    const isAdminPage = window.location.pathname.includes('/admin');
    
    if (adminToken && isAdminPage) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }
}

// Create singleton instance
const tokenRefreshService = new TokenRefreshService();

// Export singleton
export default tokenRefreshService;